import { GRADE_SCALE_PRESETS } from '@repo/lib/constants/grade-scale-constants';
import { gradeMappingCodec, type GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';
import { AwardIcon, CheckCircle2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';

import type { GradeBand, GradeScheme } from './gradeScale.types';

import { getMappingBands } from './gradeScaleBands';
import { GradeScaleEditorContent } from './GradeScaleEditorContent';
import { useGradeScaleEditor } from './useGradeScaleEditor';

export interface GradeMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialGradeMapping: GradeMappingSchema | null;
  onConfirm: (gradeMapping: GradeMappingSchema | null) => void;
}

/**
 * Seeds the editor with the built-in presets, pre-loading the saved mapping's
 * thresholds onto the preset whose grades match it so existing values survive
 * a round-trip. A mapping that no longer satisfies the scale rules is ignored.
 */
function buildInitialSchemes(mapping: GradeMappingSchema | null): {
  schemes: GradeScheme[];
  selectedIndex: number;
} {
  const savedBands: GradeBand[] | null = getMappingBands(mapping);
  if (!savedBands) {
    return { schemes: GRADE_SCALE_PRESETS, selectedIndex: 0 };
  }
  const savedLabels: string = savedBands
    .map((band) => band.name)
    .sort()
    .join('|');
  const matchIndex: number = GRADE_SCALE_PRESETS.findIndex(
    (preset) =>
      preset.bands
        .map((band) => band.name)
        .sort()
        .join('|') === savedLabels
  );
  const targetIndex: number = matchIndex === -1 ? 0 : matchIndex;
  const schemes: GradeScheme[] = GRADE_SCALE_PRESETS.map((preset, index) => (index === targetIndex ? { ...preset, bands: savedBands } : preset));
  return { schemes, selectedIndex: targetIndex };
}

interface GradeMappingDialogBodyProps {
  initialGradeMapping: GradeMappingSchema | null;
  onConfirm: (gradeMapping: GradeMappingSchema | null) => void;
  onCancel: () => void;
}

function GradeMappingDialogBody(gradeMappingDialogBodyProps: GradeMappingDialogBodyProps) {
  const { initialGradeMapping, onConfirm, onCancel } = gradeMappingDialogBodyProps;

  const initial = useMemo(() => buildInitialSchemes(initialGradeMapping), [initialGradeMapping]);
  const editor = useGradeScaleEditor(initial.schemes, initial.selectedIndex);

  const handleSave = () => {
    if (!editor.selectedScheme) {
      return;
    }
    const result = gradeMappingCodec.safeDecode(editor.selectedScheme.bands);
    if (!result.success) {
      toast.error('Popraw skalę ocen');
      return;
    }
    onConfirm(result.data);
  };

  return (
    <>
      <DialogBody>
        <DialogHeader className="border-border-primary border-b">
          <FeaturedIcon size="lg">
            <AwardIcon />
          </FeaturedIcon>
          <div className="flex min-w-0 flex-col gap-0.5">
            <DialogTitle>Skala ocen</DialogTitle>
            <DialogDescription>Edytuj skalę ocen</DialogDescription>
          </div>
        </DialogHeader>
        <div className="flex grow flex-col overflow-y-auto p-4">
          <GradeScaleEditorContent editor={editor} />
        </div>
      </DialogBody>
      <DialogFooter className="border-border-primary bg-accent dark:bg-card border-t">
        <Button type="button" variant="outline" className="mr-auto" onClick={() => onConfirm(null)}>
          Bez oceniania
        </Button>
        <Button type="button" variant="tertiary" onClick={onCancel}>
          Anuluj
        </Button>
        <Button type="button" onClick={handleSave}>
          <CheckCircle2Icon />
          Zapisz
        </Button>
      </DialogFooter>
    </>
  );
}

export function GradeMappingDialog(gradeMappingDialogProps: GradeMappingDialogProps) {
  const { open, onOpenChange, initialGradeMapping, onConfirm } = gradeMappingDialogProps;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">{open && <GradeMappingDialogBody initialGradeMapping={initialGradeMapping} onConfirm={onConfirm} onCancel={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}
